import { useMediaQuery } from 'react-responsive';
import { AlarmFillIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import '@/shared/plan/events.quiz-trial-discount';
import { trackEvent } from '@zep/logger/tracking';
import { BoltFillIcon, XIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { useCountryCode } from '@/shared/hooks/useCountryCode';
import { type TrialEndDiscountState } from '@/shared/plan/trialEndDiscount';
import { getPaymentEntryUrl } from '@/shared/utils/paymentEntryUrl';
import { Button, IconButton } from '@/ui_v3';
import { DialogTitle, ResponsiveDialog } from '@/ui_v3/components/dialog';
import { cn } from '@/utils/tailwind.util';
import { MODAL_BULLET_ORDER, TrialEndDiscountBullets } from './TrialEndDiscountBullets';
import { useTrialEndCountdownText } from './useTrialEndCountdownText';
const DISCOUNT_IMAGES = {
  ko: '/assets/trial-end-discount-ko.webp',
  en: '/assets/trial-end-discount-en.webp',
  ja: '/assets/trial-end-discount-ja.webp',
  id: '/assets/trial-end-discount-id.webp',
  th: '/assets/trial-end-discount-th.webp',
  'zh-CN': '/assets/trial-end-discount-zh.webp',
  'zh-HK': '/assets/trial-end-discount-zh.webp',
  'zh-TW': '/assets/trial-end-discount-zh.webp'
};
export const QUOTE_WITH_DISCOUNT_URL = '/pricing/quote?discount=trial';
export interface TrialEndDiscountModalProps {
  discount: TrialEndDiscountState;
  onClose: () => void;
}

/** 상단(데스크톱)·이미지 하단(모바일) 카운트다운 pill (Figma 29139:2449 / 29097:7667). */
function CountdownPill({
  remainingMs,
  className
}: {
  remainingMs: number;
  className?: string;
}) {
  const {
    withPrefix
  } = useTrialEndCountdownText(remainingMs);
  return <div className={cn('flex w-full flex-col items-start bg-strong-alternative px-lg py-4xs', className)} data-sentry-component="CountdownPill" data-sentry-source-file="TrialEndDiscountModal.tsx">
      <div className="flex w-full items-center justify-center gap-2xs rounded-sm px-xs py-2xs">
        <AlarmFillIcon size="sm" fill="#6758FF" data-sentry-element="AlarmFillIcon" data-sentry-source-file="TrialEndDiscountModal.tsx" />
        <span className="text-[16px] font-semibold leading-[22.4px] text-strong">
          {withPrefix}
        </span>
      </div>
    </div>;
}

/**
 * 체험 종료 후 7일 20% 할인 모달 (PRD 솔루션 6).
 * Figma laptopSm 29139:2449 / tabletSm 29097:7665 / mobileLg 29097:7667 / mobileSm 29097:7666.
 */
export function TrialEndDiscountModal({
  discount,
  onClose
}: TrialEndDiscountModalProps) {
  const {
    t,
    i18n
  } = useTranslation(['common']);
  // Figma mobileLg is 561px wide, while the shared viewport hook labels 561px as tablet.
  const isMobileLayout = useMediaQuery({
    query: '(max-width: 561px)'
  });
  const router = useRouter();
  const {
    user
  } = useUser();
  const {
    countryCode,
    isKR
  } = useCountryCode();
  const countdownRemainingMs = discount.remainingMs;

  // 데스크톱은 Figma 대로 두 줄(whitespace-pre-line), 모바일은 한 줄이라
  // 기본 white-space 가 개행을 공백으로 접는다 (Figma 29139:2459 / 29097:7667).
  const title = t('common:trialEndDiscount.modal.title');
  const description = <Trans t={t} i18nKey="common:trialEndDiscount.modal.description" components={{
    Strong: <strong className={isMobileLayout ? 'text-[14px] font-semibold leading-[19.6px]' : 'text-body-lg-bold'} />
  }} />;
  const discountImage = DISCOUNT_IMAGES[i18n.language as keyof typeof DISCOUNT_IMAGES] ?? DISCOUNT_IMAGES.en;
  const artwork = <div className="size-full overflow-hidden rounded-lg bg-neutral">
      <img className="size-full object-cover" src={discountImage} alt="" />
    </div>;
  const bullets = <TrialEndDiscountBullets order={MODAL_BULLET_ORDER} maxUserCount={discount.maxUserCount} maxMonthCount={discount.maxMonthCount} />;
  const goQuote = () => {
    trackEvent('quiz_trial_discount_cta_click', {
      cta: 'quote',
      phase: 'post-expiry'
    });
    onClose();
    void router.push(QUOTE_WITH_DISCOUNT_URL);
  };
  const goCheckout = () => {
    trackEvent('quiz_trial_discount_cta_click', {
      cta: 'purchase',
      phase: 'post-expiry'
    });
    onClose();
    void router.push(getPaymentEntryUrl({
      kind: 'purchase',
      plan: 'PRO'
    }, user?.countryCode ?? countryCode));
  };
  const closeButton = <IconButton aria-label={t('common:global.close.button')} variant={isMobileLayout ? 'transparent' : 'contained'} intent="neutral" size="sm" rounded="full" className={isMobileLayout ? 'ml-auto shrink-0' : 'absolute -top-2 right-md z-10'} onClick={() => {
    trackEvent('quiz_trial_discount_cta_click', {
      cta: 'close',
      phase: 'post-expiry'
    });
    onClose();
  }}>
      <XIcon size="md" />
    </IconButton>;
  const actions = <div className="flex w-full items-center gap-xs mobile-lg:items-start">
      {isKR && <Button variant="contained" intent="neutral" size={isMobileLayout ? 'md' : 'lg'} className={isMobileLayout ? 'h-10 w-[140px] shrink-0 mobile-lg:w-[180px]' : 'h-12 flex-1'} onClick={goQuote}>
          {t('common:trialEndDiscount.cta.quote')}
        </Button>}
      <Button variant="contained" intent="gradient" size={isMobileLayout ? 'md' : 'lg'} className={isMobileLayout ? 'h-10 flex-1' : isKR ? 'h-12 w-[400px] shrink-0' : 'h-12 w-full'} leftSlot={<BoltFillIcon size="md" fill="#FFF" />} onClick={goCheckout}>
        {t('common:trialEndDiscount.cta.purchase')}
      </Button>
    </div>;
  return <ResponsiveDialog size="lg" isMobileLayout={isMobileLayout} className={isMobileLayout ? 'border-0 max-[561px]:!w-full' : 'gap-lg overflow-y-auto'} onClose={onClose} data-sentry-element="ResponsiveDialog" data-sentry-component="TrialEndDiscountModal" data-sentry-source-file="TrialEndDiscountModal.tsx">
      {isMobileLayout ? <>
          <header className="flex min-h-[60px] w-full items-center px-md pb-xs pt-md">
            <DialogTitle className="text-heading-md-bold text-default">
              {title}
            </DialogTitle>
            {closeButton}
          </header>
          <main className="flex min-h-0 w-full flex-1 flex-col items-center gap-sm overflow-y-auto px-md">
            <p className="w-full text-body-md-medium text-default">
              {description}
            </p>
            <div className="w-full">{bullets}</div>
            <div className="h-[460px] w-[388px] max-w-full shrink-0 mobile-lg:w-[390px]">
              {artwork}
            </div>
            <CountdownPill remainingMs={countdownRemainingMs} className="rounded-sm" />
          </main>
          <footer className="flex w-full shrink-0 flex-col items-center justify-center gap-xs px-md py-md">
            {actions}
          </footer>
        </> : <>
          <CountdownPill remainingMs={countdownRemainingMs} />
          <main className="relative flex w-full shrink-0 items-start justify-between px-lg">
            <div className="flex min-w-0 max-w-[272px] flex-1 flex-col gap-md px-md pt-md">
              <DialogTitle className="whitespace-pre-line text-heading-xl-extrabold text-default opacity-90">
                {title}
              </DialogTitle>
              <p className="whitespace-pre-line text-body-lg-medium text-default opacity-90">
                {description}
              </p>
              {bullets}
            </div>
            <div className="h-[505px] w-[400px] shrink-0">{artwork}</div>
            {closeButton}
          </main>
          <footer className="flex h-[88px] w-full shrink-0 flex-col items-center justify-end gap-md border-t border-alternative px-lg pb-lg pt-md shadow-lg">
            {actions}
          </footer>
        </>}
    </ResponsiveDialog>;
}